import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, API_BASE } from "../../lib/api";
import { MapPin, MapPinOff, WifiOff, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtT = (d) => d ? new Date(d).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
const sendState = (state) => fetch(`${API_BASE}/gps/me/state`, { method: "POST", credentials: "include", keepalive: true,
  headers: { "Content-Type": "application/json" }, body: JSON.stringify({ state }) }).catch(() => {});

function ConsentCard({ onAnswer, busy }) {
  return (
    <div className="rounded-2xl border border-sky-500/40 bg-[#06101E] p-4 space-y-3" data-testid="gps-consent-card">
      <div className="flex items-center gap-2 text-sky-300 font-bold"><ShieldCheck size={18} />Informativa localizzazione GPS</div>
      <div className="text-xs text-slate-300 space-y-1.5 leading-relaxed">
        <p>Durante il turno di lavoro Striano NCC rileva la posizione del telefono per coordinare i servizi e per la sicurezza di autisti e clienti.</p>
        <p>La posizione viene inviata ogni 20 secondi circa, <b>solo dall'inizio turno (controllo di inizio turno) alla fine turno (controllo di fine turno)</b> e solo con l'app aperta. Fuori turno non viene raccolto nulla.</p>
        <p>Viene conservata solo l'ultima posizione, senza storico dei percorsi, e cancellata 12 ore dopo la fine del turno. La vedono solo amministratori e operatori della centrale.</p>
        <p>Puoi revocare il permesso in qualsiasi momento dalle impostazioni del telefono o del browser. I servizi restano sempre consultabili.</p>
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={busy} onClick={() => onAnswer(true)} data-testid="gps-consent-accept"
          className="flex-1 h-11 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold disabled:opacity-60">Consento</button>
        <button type="button" disabled={busy} onClick={() => onAnswer(false)} data-testid="gps-consent-decline"
          className="h-11 px-4 rounded-xl border border-slate-700 text-slate-300">Non ora</button>
      </div>
    </div>
  );
}

function StatusBar({ tone, icon: I, title, sub, testid }) {
  const tones = { live: "border-sky-500/50 bg-sky-500/10 text-sky-200", warn: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    bad: "border-rose-500/40 bg-rose-500/10 text-rose-200", off: "border-slate-700 bg-slate-900/60 text-slate-300" };
  return (
    <div className={`rounded-2xl border px-4 py-3 flex items-center gap-3 ${tones[tone]}`} data-testid={testid}>
      <span className="relative flex-shrink-0"><I size={20} />{tone === "live" && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-sky-400 animate-ping" />}</span>
      <div className="min-w-0"><div className="text-sm font-bold">{title}</div>{sub && <div className="text-xs opacity-80">{sub}</div>}</div>
    </div>
  );
}

export function GpsTracker() {
  const [st, setSt] = useState(null);
  const [geo, setGeo] = useState("idle");
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [lastSent, setLastSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const watchRef = useRef(null);
  const fixRef = useRef(null);
  const sentTsRef = useRef(0);
  const geoRef = useRef("idle");

  const load = useCallback(async () => {
    try { setSt((await api.get("/gps/me/status")).data); } catch { setSt(null); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const on = () => { setOnline(true); load(); }, off = () => setOnline(false);
    window.addEventListener("gps-refresh", load); window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { clearInterval(t); window.removeEventListener("gps-refresh", load); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [load]);

  const tracking = !!(st?.active && st?.consent);
  const [retry, setRetry] = useState(0);
  const inApp = /FBAN|FBAV|Instagram|WhatsApp|Line\/|Telegram|; wv\)/i.test(navigator.userAgent || "");
  const setGeoState = (g) => { geoRef.current = g; setGeo(g); };

  useEffect(() => {
    if (!tracking) { return undefined; }
    if (!("geolocation" in navigator)) { setGeoState("unsupported"); sendState("unavailable"); return undefined; }
    setGeoState("searching");
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => { fixRef.current = p; if (geoRef.current !== "ok") setGeoState("ok"); },
      (e) => {
        const g = e.code === 1 ? "denied" : "unavailable";
        if (geoRef.current !== g) { setGeoState(g); sendState(g); }
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 });
    const send = async () => {
      const p = fixRef.current;
      if (!p || p.timestamp <= sentTsRef.current || !navigator.onLine || document.visibilityState !== "visible") return;
      try {
        const c = p.coords;
        await api.post("/gps/me/ping", { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy, speed: c.speed, heading: c.heading, ts: p.timestamp });
        sentTsRef.current = p.timestamp; setLastSent(new Date());
      } catch (e) {
        if (e?.response?.status === 409 || e?.response?.status === 403) load();
      }
    };
    const iv = setInterval(send, (st.interval_s || 20) * 1000);
    const first = setTimeout(send, 3000);
    const vis = () => { if (document.visibilityState === "hidden") sendState("background"); else { sendState("foreground"); send(); } };
    document.addEventListener("visibilitychange", vis);
    return () => {
      clearInterval(iv); clearTimeout(first); document.removeEventListener("visibilitychange", vis);
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null; fixRef.current = null; setGeoState("idle");
    };
  }, [tracking, st?.interval_s, load, retry]);

  const answer = async (accept) => {
    setBusy(true);
    if (accept && "geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((p) => { fixRef.current = p; }, (e) => { if (e.code === 1) { setGeoState("denied"); } },
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 });
    }
    try { await api.post("/gps/me/consent", { accept }); if (!accept) toast("GPS non attivato. Puoi consentirlo più tardi."); await load(); }
    catch { toast.error("Impossibile salvare la scelta"); }
    setBusy(false);
  };

  if (!st) return null;
  if (!st.active) {
    return <StatusBar tone="off" icon={MapPinOff} testid="gps-status-off" title="GPS disattivato"
      sub={st.has_shift ? "Si attiva con il controllo di inizio turno e si ferma con quello di fine turno." : "Nessun turno Giorno/Notte pianificato oggi: la posizione non viene rilevata."} />;
  }
  if (!st.consent) return <ConsentCard onAnswer={answer} busy={busy} />;
  if (!window.isSecureContext || !("geolocation" in navigator)) return <StatusBar tone="bad" icon={MapPinOff} testid="gps-status-insecure" title="Questo browser non può usare il GPS"
    sub="Apri l'Area Driver direttamente in Safari (iPhone) o Chrome (Android) all'indirizzo https del gestionale, non da un link dentro WhatsApp o altre app." />;
  if (!online) return <StatusBar tone="warn" icon={WifiOff} testid="gps-status-offline" title="Connessione assente" sub={`La centrale vede l'ultima posizione inviata (${fmtT(lastSent)}).`} />;
  if (geo === "denied") return (
    <div className="space-y-2">
      <StatusBar tone="bad" icon={MapPinOff} testid="gps-status-denied" title="Permesso posizione negato"
        sub={inApp ? "Stai usando il browser interno di un'altra app (es. WhatsApp): apri il gestionale in Safari o Chrome."
          : /iPhone|iPad/i.test(navigator.userAgent) ? "iPhone: Impostazioni › Privacy e sicurezza › Localizzazione: attiva, poi Siti web Safari › «Mentre usi l'app». In Safari: aA › Impostazioni sito web › Posizione › Consenti."
          : "Android: in Chrome tocca il lucchetto accanto all'indirizzo › Autorizzazioni › Posizione › Consenti. Verifica che la Posizione del telefono sia accesa."} />
      <button type="button" onClick={() => { setGeoState("idle"); setRetry(r => r + 1); }} data-testid="gps-retry"
        className="w-full h-10 rounded-xl border border-sky-500/50 text-sky-300 text-sm font-bold">Ho attivato il permesso: riprova</button>
    </div>
  );
  if (geo === "unavailable" || geo === "unsupported") return <StatusBar tone="warn" icon={MapPinOff} testid="gps-status-unavailable" title="GPS non disponibile" sub="Segnale GPS assente o localizzazione disattivata sul telefono." />;
  if (geo === "searching" && !lastSent) return <StatusBar tone="warn" icon={Loader2} testid="gps-status-searching" title="Ricerca posizione GPS…" sub={`Turno attivo${st.vehicle_plate ? ` · ${st.vehicle_plate}` : ""}`} />;
  return <StatusBar tone="live" icon={MapPin} testid="gps-status-live" title={`GPS ATTIVO · turno in corso${st.vehicle_plate ? ` · ${st.vehicle_plate}` : ""}`}
    sub={`Ultimo invio ${fmtT(lastSent)} · tieni l'app aperta: con schermo bloccato il telefono sospende il GPS.`} />;
}
